import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { usePosts }             from '../../hooks/usePosts'
import { PostStatusBadge }      from './PostStatusBadge'
import { DeleteConfirmModal }   from './DeleteConfirmModal'
import { Button }               from '../ui/Button'
import { Input }                from '../ui/Input'
import { Select }               from '../ui/Select'
import { Spinner }              from '../ui/Spinner'
import { formatDateTime }       from '../../utils/htmlUtils'
import type { Post, PostStatus } from '../../types'

const STATUS_FILTER = [
  { value: 'all',       label: 'All statuses' },
  { value: 'published', label: 'Published' },
  { value: 'draft',     label: 'Draft' },
]

const PAGE_SIZE = 15

export function PostsTable() {
  const navigate = useNavigate()
  const { data, loading, fetch, deletePost } = usePosts()

  const [search,     setSearch]     = useState('')
  const [statusFilter, setStatus]   = useState<'all' | PostStatus>('all')
  const [page,       setPage]       = useState(1)
  const [deleting,   setDeleting]   = useState<Post | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const load = useCallback(() => {
    fetch({
      q:        search || undefined,
      status:   statusFilter,
      page,
      pageSize: PAGE_SIZE,
    })
  }, [fetch, search, statusFilter, page])

  useEffect(() => { load() }, [load])

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1) }, [search, statusFilter])

  const handleDelete = async () => {
    if (!deleting) return
    setDeleteLoading(true)
    const ok = await deletePost(deleting.slug)
    setDeleteLoading(false)
    if (ok) { setDeleting(null); load() }
  }

  const posts      = data?.data ?? []
  const totalPages = data?.meta.totalPages ?? 1
  const total      = data?.meta.totalCount ?? 0

  return (
    <>
      {/* Filters */}
      <div className="pt-filters">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search posts…"
          prefix="⌕"
          className="pt-search"
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => setStatus(e.target.value as typeof statusFilter)}
          className="pt-status-filter"
        />
        <Button
          variant="primary"
          size="sm"
          onClick={() => navigate('/posts/new')}
          icon="+"
        >
          New Post
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="pt-loading"><Spinner label="Loading posts…" /></div>
      ) : posts.length === 0 ? (
        <div className="pt-empty">
          {search ? `No posts matching "${search}"` : 'No posts found.'}
        </div>
      ) : (
        <div className="pt-table-wrap">
          <table className="pt-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Category</th>
                <th>Published</th>
                <th>Views</th>
                <th className="pt-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id}>
                  <td>
                    <div className="pt-post-title">{post.title}</div>
                    <div className="pt-post-slug">/{post.slug}</div>
                    {post.tags.length > 0 && (
                      <div className="pt-post-tags">
                        {post.tags.slice(0, 4).map((t) => (
                          <span key={t} className="pt-tag">{t}</span>
                        ))}
                        {post.tags.length > 4 && (
                          <span className="pt-tag pt-tag--more">+{post.tags.length - 4}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td><PostStatusBadge status={post.status} /></td>
                  <td className="pt-category">{post.category}</td>
                  <td className="pt-date">{formatDateTime(post.publishedAt)}</td>
                  <td className="pt-views">{post.viewCount.toLocaleString()}</td>
                  <td>
                    <div className="pt-row-actions">
                      <Link to={`/posts/${post.slug}/edit`} className="pt-action-link">
                        Edit
                      </Link>
                      <button
                        className="pt-action-delete"
                        onClick={() => setDeleting(post)}
                        aria-label={`Delete ${post.title}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pt-pagination">
          <span className="pt-pagination-info">
            {total} posts · page {page} of {totalPages}
          </span>
          <div className="pt-pagination-btns">
            <Button
              size="sm"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >← Prev</Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >Next →</Button>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        open={!!deleting}
        postTitle={deleting?.title ?? ''}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />

      <style>{`
        .pt-filters {
          display: flex;
          align-items: flex-end;
          gap: 10px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .pt-search { flex: 1; min-width: 200px; }
        .pt-status-filter { width: 160px; }
        .pt-loading {
          display: flex;
          justify-content: center;
          padding: 60px 0;
        }
        .pt-empty {
          text-align: center;
          padding: 60px 0;
          color: var(--text-muted);
          font-size: 0.9rem;
        }
        .pt-table-wrap {
          overflow-x: auto;
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
        }
        .pt-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }
        .pt-table thead th {
          padding: 12px 16px;
          text-align: left;
          font-size: 0.72rem;
          font-weight: 700;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: var(--bg-surface);
          border-bottom: 1px solid var(--border);
          white-space: nowrap;
        }
        .pt-table tbody tr {
          border-bottom: 1px solid var(--border-light);
          transition: background 0.12s;
        }
        .pt-table tbody tr:last-child { border-bottom: none; }
        .pt-table tbody tr:hover { background: var(--bg-hover); }
        .pt-table td { padding: 12px 16px; vertical-align: middle; }
        .pt-post-title {
          font-weight: 600;
          color: var(--text-primary);
          line-height: 1.3;
          max-width: 340px;
        }
        .pt-post-slug {
          font-size: 0.72rem;
          color: var(--text-muted);
          font-family: var(--font-mono);
          margin-top: 2px;
        }
        .pt-post-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-top: 5px;
        }
        .pt-tag {
          padding: 1px 7px;
          background: var(--bg-hover);
          color: var(--text-muted);
          border-radius: 99px;
          font-size: 0.68rem;
          font-weight: 500;
        }
        .pt-tag--more { color: var(--text-muted); }
        .pt-category {
          color: var(--text-secondary);
          text-transform: capitalize;
          white-space: nowrap;
        }
        .pt-date {
          color: var(--text-secondary);
          white-space: nowrap;
          font-size: 0.8rem;
        }
        .pt-views {
          color: var(--text-secondary);
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .pt-actions-col { text-align: right; }
        .pt-row-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          align-items: center;
        }
        .pt-action-link {
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--accent);
          text-decoration: none;
          padding: 4px 8px;
          border-radius: var(--radius-sm);
          transition: background 0.12s;
        }
        .pt-action-link:hover { background: var(--accent-subtle); text-decoration: none; }
        .pt-action-delete {
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--color-error);
          padding: 4px 8px;
          border-radius: var(--radius-sm);
          transition: background 0.12s;
        }
        .pt-action-delete:hover { background: var(--bg-error); }
        .pt-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 16px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .pt-pagination-info {
          font-size: 0.8rem;
          color: var(--text-muted);
        }
        .pt-pagination-btns { display: flex; gap: 8px; }
      `}</style>
    </>
  )
}
